import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';

import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Container from '@material-ui/core/Container';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Typography } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import { formatAmountValue } from 'helpers/helpers';
import compose from 'helpers/compose';
import Background from 'components/Layout/Background';
import Layout from 'components/Layout';

import { RootStore } from 'stores/rootStore';
import { RankingsStore } from 'stores/advisor/rankingsStore';
import { EdwardColors } from 'enums';
import FilterMenu from 'components/FilterMenu/FilterMenu';
//import { AdvisorMenuItems } from 'components/AdvisorMenu/AdvisorMenu';

interface Props {
  rootStore: RootStore;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      background: EdwardColors.GRAY_DARKER,
      '& .MuiTableCell-head': { color: '#fff' },
      '& th': {
        textAlign: 'center',
        verticalAlign: 'middle',
        position: 'relative',
        paddingRight: '20px'
      },
      '& th svg': {
        position: 'absolute',
        right: 0,
        top: '50%',
        marginTop: '-12px',
        color: EdwardColors.MEDIUM
      },
      '& th:first-child': { textAlign: 'left' },
    },
    rank: {
      '& td': { borderBottom: 'solid 1px #aaa', textAlign: 'center', paddingRight: '20px' },
      '& td:first-child': { textAlign: 'left' },
      '&.rank_1': { background: EdwardColors.DARK },
      '&.rank_2': { background: EdwardColors.MEDIUM },
      '&.rank_3': { background: EdwardColors.LIGHT },
      '&.rank_10 td': { borderBottom: 'solid 2px #999' },
      '&.rank_below10': { background: '#eee' },
      '&.current td': { color: EdwardColors.SECONDARY, fontWeight: 'bold' },
    },
    total: {
      padding: theme.spacing(2),
      color: '#666',
      fontWeight: 'bold',
    },
    additional: {
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    container: {
      maxWidth: '960px',
    },
  }),
);

const periods = [
  { value: 'ALL_TIME', label: 'accountDetail_graph_currentValues' },
  { value: 'THIS_YEAR', label: 'accountDetail_graph_thisYear' },
  { value: 'LAST_3M', label: 'accountDetail_graph_3months' },
  { value: 'LAST_6M', label: 'accountDetail_graph_6months' },
  { value: 'LAST_YEAR', label: 'accountDetail_graph_year' },
];

const Rankings: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [store] = useState(new RankingsStore(rootStore));
  const { data } = store;

  useEffect(() => {
    store.fetchData();
  }, [store]);

  return (
    <Layout title={t('advisor_rankings')}>
      <Container className={classes.container}>
        <Background>
          <Typography variant="h1" style={{ textAlign: 'center', fontWeight: 'bold' }}>
            {t('advisor_rankings').toUpperCase()}
          </Typography>
          <FilterMenu onClick={store.setPeriod} items={periods} currentValue={store.period} mobile="640" />
          {data &&
            (data.rankings ? (
              <>
                <Table>
                  <TableHead>
                    <TableRow className={classes.header}>
                      <TableCell>{t('advisor_rankings_rank')}</TableCell>
                      <TableCell style={{ width: '120px', cursor: 'pointer' }} onClick={() => store.setOrderBy('AUM')}>
                        {t('general_aum')}
                        { store.orderBy === 'AUM' && <KeyboardArrowDownIcon />}
                      </TableCell>
                      <TableCell
                        style={{ width: '135px', cursor: 'pointer' }}
                        onClick={() => store.setOrderBy('PF')}
                        className={classes.additional}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t('advisor_rankings_portfolioAum', { interpolation: { escapeValue: false } }),
                          }}
                        />
                        { store.orderBy === 'PF' && <KeyboardArrowDownIcon />}
                      </TableCell>
                      <TableCell style={{ width: '120px', cursor: 'pointer' }} onClick={() => store.setOrderBy('ACC')}>
                        {t('advisor_rankings_accounts')}
                        { store.orderBy === 'ACC' && <KeyboardArrowDownIcon />}
                      </TableCell>
                      <TableCell
                        style={{ width: '120px', cursor: 'pointer' }}
                        onClick={() => store.setOrderBy('INV')}
                        className={classes.additional}
                      >
                        {t('accounts_overview_totalRegularInvestment')}
                        { store.orderBy === 'INV' && <KeyboardArrowDownIcon />}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rankings.map((rank) => (
                      <TableRow
                        key={`rank_${rank.rank}`}
                        className={`${classes.rank} rank_${rank.rank} ${rank.rank > 10 ? 'rank_below10' : ''} ${
                          rank.current ? 'current' : ''
                        }`}
                      >
                        <TableCell>
                          <strong>
                            {rank.rank}.
                            {rank.current && (
                              <>
                                {' '}
                                {rootStore.stores.userStore.loggedUser!.firstname}{' '}
                                {rootStore.stores.userStore.loggedUser!.lastname}
                              </>
                            )}
                          </strong>
                        </TableCell>
                        <TableCell>{formatAmountValue(rank.aum.value, rank.aum.currency, false, true)}</TableCell>
                        <TableCell className={classes.additional}>
                          {formatAmountValue(rank.portfolioAum.value, rank.portfolioAum.currency, false, true)}
                        </TableCell>
                        <TableCell>{rank.accounts}</TableCell>
                        <TableCell className={classes.additional}>
                          {formatAmountValue(rank.investments.value, rank.aum.currency, false, true)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className={classes.total}>
                  {t('advisor_rankings_total')}: {data.total}
                </div>
              </>
            ) : (
              <Typography variant="h3" style={{ textAlign: 'center', padding: '40px' }}>
                {t('advisor_rankings_noData')}
              </Typography>
            ))}
        </Background>
      </Container>
    </Layout>
  );
};

export default compose(inject('rootStore'), observer)(Rankings);
